import React, { useState, useRef, useEffect } from 'react';
import '../../routes/root.css';
import './chatbot.css';
import { sendMessage, createSession, updateSession, deleteSession, listSessions, getMessages, reprocessMessage as reprocessInternalMessage } from '../../apis/internalChatApi';
import { useAuth } from '../AuthProvider';
import { fetchUser } from '../../apis/usersApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout, Button, Drawer, List, Avatar, Input, Typography, Space, Tooltip, message as antdMessage, Spin } from 'antd';
import { MenuOutlined, CloseOutlined, DeleteOutlined, PlusOutlined, SendOutlined, EditOutlined, PushpinOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Sider, Content } = Layout;
const { TextArea } = Input;

const Chatbot = () => {
  const { authenticatedUser } = useAuth() || {};
  const [userName, setUserName] = useState('');

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingConv, setLoadingConv] = useState(false);
  const bottomRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [draggingId, setDraggingId] = useState(null);
  const [availableHeight, setAvailableHeight] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  useEffect(() => {
    if (authenticatedUser) {
      syncWithBackend();
    }
  }, [authenticatedUser]);

  const syncWithBackend = async () => {
    try {
      const res = await listSessions({ user_id: authenticatedUser });
      const remoteSessions = res?.data || [];
      if (remoteSessions.length === 0) {
        setConversations([]);
        return;
      }

      // Map remote sessions to conversation objects
      const nextConversations = remoteSessions.map(rs => ({
        id: `conv_${rs.session_id}`,
        session_id: rs.session_id,
        title: rs.title || 'Conversación',
        pinned: rs.pinned || false,
        created_at: new Date(rs.updated_at).getTime() || Date.now(),
        messages: [] // messages will be loaded on demand
      }));

      nextConversations.sort((a,b) => (b.pinned === true) - (a.pinned === true) || b.created_at - a.created_at);
      setConversations(nextConversations);
      
      // If no active conv, set to the first one
      if (!activeConvId && nextConversations.length > 0) {
        loadConversation(nextConversations[0]);
      }
    } catch (e) {
      console.error("Error syncing conversations:", e);
    }
  };

  useEffect(() => {
    if (authenticatedUser) {
      (async () => {
        try {
          const u = await fetchUser(authenticatedUser);
          setUserName(u?.name || u?.username || '');
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [authenticatedUser]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const compute = () => {
      const headerEl = document.querySelector('.w-100');
      const footerEl = document.querySelector('.themed-footer');
      const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;
      const footerHeight = footerEl ? footerEl.getBoundingClientRect().height : 0;
      const h = window.innerHeight - headerHeight - footerHeight;
      setAvailableHeight(h > 200 ? h : null);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const persistConversations = (next) => {
    setConversations(next);
  };

  const createNewConversation = async () => {
    const id = `conv_${Date.now()}`;
    const conv = { id, title: 'Nueva conversación', created_at: Date.now(), messages: [], pinned: false };
    // If user is authenticated, create a server session to link messages
    if (authenticatedUser) {
      try {
        // send title and pinned flags so server session mirrors client conversation
        const res = await createSession({ user_id: authenticatedUser, title: conv.title, pinned: conv.pinned });
        const session_id = res?.data?.session_id || res?.data?.sessionId;
        if (session_id) conv.session_id = session_id;
      } catch (e) {
        // ignore session creation errors; conversation stays local
      }
    }
    const next = [conv, ...conversations];
    persistConversations(next);
    setActiveConvId(id);
    setMessages([]);
    if (isMobile) setSidebarOpen(false);
  };

  const loadConversation = async (conv) => {
    setActiveConvId(conv.id);
    if (conv.session_id) {
      setLoadingConv(true);
      try {
        const res = await getMessages(conv.session_id);
        const remoteMessages = res?.data || [];
        // Map backend message structure to frontend Role/Text/Metadata
        const mapped = remoteMessages.map(rm => ({
          role: rm.sender === 'bot' ? 'assistant' : 'user',
          text: rm.text,
          ts: new Date(rm.created_at).getTime(),
          message_id: rm.message_id,
          queries: rm.metadata?.queries || []
        }));
        setMessages(mapped);
        
        // Update local state messages list too
        setConversations(prev => prev.map(c => c.id === conv.id ? { ...c, messages: mapped } : c));
      } catch (err) {
        console.error("Error loading remote messages:", err);
        setMessages(conv.messages || []);
      } finally {
        setLoadingConv(false);
      }
    } else {
      setMessages(conv.messages || []);
    }
    if (isMobile) setSidebarOpen(false);
  };

  // Updates the in-memory message list for the active conversation
  const saveActiveConversation = (updatedMessages) => {
    setConversations(prev => prev.map(c =>
      c.id === activeConvId ? { ...c, messages: updatedMessages } : c
    ));
  };

  const doSend = async () => {
    const text = input.trim();
    if (!text) return;
    const userMsg = { role: 'user', text, ts: Date.now() };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    try {
      const activeConv = conversations.find(c => c.id === activeConvId);
      const session_id = activeConv?.session_id;
      const res = await sendMessage({ message: text, session_id, user_id: authenticatedUser });
      const reply = res && res.data && (res.data.reply || res.data.answer || res.data.data) ? (res.data.reply || res.data.answer || JSON.stringify(res.data.data)) : 'Sin respuesta';
      const queries = res?.data?.queries || [];
      const assistantMsg = { role: 'assistant', text: reply, ts: Date.now(), queries };
      // Attach server message id if provided
      const messageId = res?.data?.message_id || res?.data?.messageId;
      if (messageId) assistantMsg.message_id = messageId;
      const all = [...nextMessages, assistantMsg];
      setMessages(all);
      // If backend returned or created a session_id, persist it in the conversation
      const returnedSessionId = res?.data?.session_id || res?.data?.sessionId;
      if (!activeConvId) {
        const id = `conv_${Date.now()}`;
        const conv = { id, title: text.slice(0, 40) || 'Conversación', created_at: Date.now(), messages: all };
        if (returnedSessionId) conv.session_id = returnedSessionId;
        const next = [conv, ...conversations];
        persistConversations(next);
        setActiveConvId(id);
      } else {
        // update conversation messages and possibly session_id
        const title = conversations.find(c => c.id === activeConvId)?.title || text.slice(0,40);
        const nextConvs = conversations.map(c => c.id === activeConvId ? { ...c, messages: all, session_id: c.session_id || returnedSessionId } : c);
        persistConversations(nextConvs);
        saveActiveConversation(all, title);
      }
    } catch (err) {
      const assistantMsg = { role: 'assistant', text: 'Error: no se pudo obtener respuesta del servidor.', ts: Date.now() };
      const all = [...nextMessages, assistantMsg];
      setMessages(all);
      if (activeConvId) saveActiveConversation(all);
    } finally {
      setLoading(false);
    }
  };

  const handleReprocess = async (messageId) => {
    if (!messageId) {
      antdMessage.warning('Este mensaje aún no tiene ID del servidor. Intenta de nuevo en un momento.');
      return;
    }
    setLoading(true);
    try {
      const res = await reprocessInternalMessage(messageId);
      const reply = res?.data?.reply || 'Sin respuesta';
      const queries = res?.data?.queries || [];
      const assistantMsg = { role: 'assistant', text: reply, ts: Date.now(), queries, message_id: res?.data?.message_id };
      
      const all = [...messages, assistantMsg];
      setMessages(all);
      saveActiveConversation(all);
      antdMessage.success('Respuesta regenerada');
    } catch (err) {
      console.error('Reprocess error:', err);
      antdMessage.error('Error al reprocesar el mensaje');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doSend();
    }
  };

  const deleteConversation = (id) => {
    const next = conversations.filter(c => c.id !== id);
    persistConversations(next);
    if (id === activeConvId) {
      if (next.length > 0) {
        setActiveConvId(next[0].id);
        setMessages(next[0].messages || []);
      } else {
        setActiveConvId(null);
        setMessages([]);
      }
    }
    // If conversation had a server session, request soft-delete
    try {
      const conv = conversations.find(c => c.id === id);
      const sid = conv?.session_id;
      if (sid) {
        deleteSession(sid).catch(() => {});
      }
    } catch (e) {
      // ignore
    }
  };

  const togglePin = (id, e) => {
    if (e) e.stopPropagation();
    const convBefore = conversations.find(c => c.id === id) || {};
    const newPinned = !convBefore.pinned;
    const next = conversations.map(c => c.id === id ? { ...c, pinned: newPinned } : c);
    next.sort((a,b) => (b.pinned === true) - (a.pinned === true) || b.created_at - a.created_at);
    persistConversations(next);
    // send update to backend if session exists
    try {
      const sid = convBefore?.session_id;
      if (sid) updateSession(sid, { pinned: newPinned }).catch(() => {});
    } catch (err) {
      // ignore
    }
  };

  const startEditing = (conv, e) => {
    if (e) e.stopPropagation();
    setEditingId(conv.id);
    setEditingTitle(conv.title || '');
  };

  const saveTitle = (id) => {
    const newTitle = editingTitle || 'Sin título';
    const next = conversations.map(c => c.id === id ? { ...c, title: newTitle } : c);
    persistConversations(next);
    // update backend session title if exists
    try {
      const convBefore = conversations.find(c => c.id === id) || {};
      const sid = convBefore?.session_id;
      if (sid) updateSession(sid, { title: newTitle }).catch(() => {});
    } catch (err) {
      // ignore
    }
    setEditingId(null);
    setEditingTitle('');
  };

  const onDragStart = (e, conv) => {
    setDraggingId(conv.id);
    try { e.dataTransfer.setData('text/plain', conv.id); } catch (err) {}
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (e, targetConv) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain') || draggingId;
    if (!draggedId || draggedId === targetConv.id) return;
    const draggedIndex = conversations.findIndex(c => c.id === draggedId);
    const targetIndex = conversations.findIndex(c => c.id === targetConv.id);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const next = [...conversations];
    const [removed] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, removed);
    persistConversations(next);
    setDraggingId(null);
  };

  const onDropAtEnd = (e) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain') || draggingId;
    if (!draggedId) return;
    const next = conversations.filter(c => c.id !== draggedId);
    const dragged = conversations.find(c => c.id === draggedId);
    if (dragged) next.push(dragged);
    persistConversations(next);
    setDraggingId(null);
  };

  const activeConv = conversations.find(c => c.id === activeConvId);

  const sortedConversations = [...conversations].sort((a,b) => (b.pinned === true) - (a.pinned === true) || b.created_at - a.created_at);

  const SidebarList = (
    <div className="gp-chat-conversations-list" onDragOver={onDragOver} onDrop={onDropAtEnd}>
      {sortedConversations.length === 0 ? (
        <div className="gp-empty">No hay conversaciones guardadas</div>
      ) : (
        <List
          dataSource={sortedConversations}
          renderItem={conv => (
            <List.Item
              draggable
              onDragStart={(e) => onDragStart(e, conv)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, conv)}
              className={conv.id === activeConvId ? 'gp-conv-item active' : 'gp-conv-item'}
              onClick={() => loadConversation(conv)}
            >
              <List.Item.Meta
                avatar={<Avatar>{conv.title?.charAt(0) || 'C'}</Avatar>}
                title={(
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
                    <div style={{display:'flex', alignItems:'center', gap:8}}>
                      {editingId === conv.id ? (
                        <Input
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onBlur={() => saveTitle(conv.id)}
                          onPressEnter={() => saveTitle(conv.id)}
                          autoFocus
                          size="small"
                        />
                      ) : (
                        <div style={{display:'flex', alignItems:'center', gap:8}}>
                          <span style={{fontWeight:600}}>{conv.title || 'Sin título'}</span>
                          {conv.pinned && <PushpinOutlined style={{color:'#f59e0b'}} />}
                        </div>
                      )}
                    </div>
                    <div style={{display:'flex', alignItems:'center', gap:6}}>
                      <Tooltip title={conv.pinned ? 'Despinear' : 'Pinear'}>
                        <Button type="text" icon={<PushpinOutlined style={conv.pinned ? {color:'#f59e0b'} : {}} />} onClick={(e) => togglePin(conv.id, e)} />
                      </Tooltip>
                      <Button type="text" icon={<EditOutlined />} onClick={(e)=> startEditing(conv, e)} />
                      <Button type="text" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }} />
                    </div>
                  </div>
                )}
                description={<div style={{color:'#6b7280'}}>{new Date(conv.created_at).toLocaleString()}</div>}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <Layout className="gp-chat-root">
      {!isMobile ? (
        <Sider collapsible collapsed={siderCollapsed} collapsedWidth={72} width={320} className="gp-chat-sider" theme="light" trigger={null}>
          {siderCollapsed ? (
            <div className="gp-chat-sider-collapsed">
              <div style={{paddingTop:12, display:'flex', flexDirection:'column', alignItems:'center', gap:12}}>
                <Button icon={<MenuOutlined />} onClick={() => setSiderCollapsed(s => !s)} />
                <div style={{display:'flex', flexDirection:'column', gap:8}}>
                  {sortedConversations.slice(0,10).map(c => (
                    <Tooltip title={c.title} key={c.id} placement="right">
                      <Avatar style={{cursor:'pointer'}} onClick={() => loadConversation(c)}>{c.title?.charAt(0) || 'C'}</Avatar>
                    </Tooltip>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="gp-chat-sidebar-inner">
              <div className="gp-chat-sidebar-header">
                <Typography.Title level={5} style={{ margin: 0 }}>Conversaciones</Typography.Title>
                <Space>
                  <Button icon={<PlusOutlined />} onClick={createNewConversation} />
                  <Button icon={<MenuOutlined />} onClick={() => setSiderCollapsed(s => !s)} />
                </Space>
              </div>
              {SidebarList}
            </div>
          )}
        </Sider>
      ) : (
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setSidebarOpen(false)}
          visible={sidebarOpen}
          bodyStyle={{ padding: 0 }}
        >
          <div style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>Conversaciones</Typography.Title>
            <div style={{ flex: 1 }} />
            <Button icon={<PlusOutlined />} onClick={createNewConversation} />
            <Button icon={<CloseOutlined />} onClick={() => setSidebarOpen(false)} />
          </div>
          {SidebarList}
        </Drawer>
      )}

      <Layout style={availableHeight ? { height: `${availableHeight}px` } : undefined}>
        <div className="gp-chat-topbar antd-topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isMobile && (
              <Button icon={<MenuOutlined />} onClick={() => setSidebarOpen(s => !s)} />
            )}
            <div style={{ marginLeft: 8 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>Chat interno</Typography.Title>
              <Typography.Text type="secondary">Pregunta sobre la data</Typography.Text>
            </div>
          </div>
        </div>

        <Content className="gp-chat-content">
          <div className={`gp-chat-area ${(messages.length === 0 || loadingConv) ? 'empty' : ''}`}>
            {loadingConv ? (
              <div className="gp-center-box centered-prompt">
                <Spin size="large" tip="Cargando mensajes..." />
              </div>
            ) : messages.length === 0 ? (
              <div className="gp-center-box centered-prompt">
                <div className="gp-welcome">{userName ? `Hola ${userName}, en qué puedo ayudarte hoy?` : 'En qué puedo ayudarte hoy?'}</div>
                <div className="gp-prompt-pill">
                  <TextArea
                    className="gp-chat-input centered"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Pregunta lo que quieras..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    bordered={false}
                  />
                  <Button type="primary" shape="round" icon={<SendOutlined />} onClick={doSend} loading={loading} disabled={!input.trim()} />
                </div>
              </div>
            ) : (
              <>
                <div className="gp-chatbot-messages" role="log" aria-live="polite">
                  {messages.map((m, i) => {
                    const messageQueries = m.queries;
                    return (
                      <div key={i} className={`gp-chat-message ${m.role === 'user' || m.sender === 'user' ? 'user' : 'assistant'}`}>
                        <div className="gp-chat-bubble-container">
                          <div className="gp-chat-bubble">
                            {m.role === 'assistant' || m.sender === 'bot' ? (
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {m.text}
                              </ReactMarkdown>
                            ) : (
                              <div className="gp-msg-text">{m.text}</div>
                            )}
                            <div className="gp-msg-ts" style={{ fontSize: '0.75rem', opacity: 0.7, marginTop: 4, textAlign: 'right' }}>
                              {(() => {
                                const d = dayjs.utc(m.ts).tz(dayjs.tz.guess());
                                const isToday = d.isSame(dayjs(), 'day');
                                return isToday ? d.format('HH:mm') : d.format('DD/MM/YYYY HH:mm');
                              })()}
                            </div>
                            {messageQueries && messageQueries.length > 0 && (
                              <div className="gp-sql-details-container">
                                <details className="gp-sql-details">
                                  <summary>SQL</summary>
                                  <pre className="gp-sql-code">
                                    {messageQueries.join('\n\n')}
                                  </pre>
                                </details>
                              </div>
                            )}
                          </div>
                          {(m.role === 'user' || m.sender === 'user') && (
                            <div className="gp-msg-actions floating">
                              <Tooltip title="Regenerar respuesta">
                                <Button 
                                  type="text" 
                                  size="small" 
                                  className="gp-action-btn"
                                  icon={<ReloadOutlined />} 
                                  onClick={() => handleReprocess(m.message_id)}
                                  loading={loading}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                <div className="gp-chatbot-input gp-prompt-pill">
                  <TextArea
                    className="gp-chat-input"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe tu pregunta... (Enter para enviar)"
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    bordered={false}
                  />
                  <Button type="primary" shape="round" icon={<SendOutlined />} onClick={doSend} loading={loading} disabled={!input.trim()} />
                </div>
              </>
            )}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default Chatbot;
